import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/api/endpoints/reports';
import { formatCoords, formatDateTime } from '@/lib/utils';
import { formatAddress } from '@/lib/address';
import { RouteReplayMap } from './RouteReplayMap';
import {
  exportCsv,
  knotsToKmh,
  ReportFilters,
  ReportShell,
  ReportTable,
  ResultBar,
  useReportFilters,
} from './_shared';

export function RouteReportPage() {
  const { deviceId, setDeviceId, preset, setPreset, executed, generate } = useReportFilters('single');

  const query = useQuery({
    queryKey: ['report', 'route', executed],
    queryFn: () =>
      reportsApi.route({
        deviceId: executed!.ids[0],
        from: executed!.range.from,
        to: executed!.range.to,
      }),
    enabled: executed !== null,
  });

  const rows = useMemo(() => query.data ?? [], [query.data]);

  const handleExport = () => {
    if (!executed) return;
    exportCsv(
      `rota_${executed.range.from.slice(0, 10)}_${executed.range.to.slice(0, 10)}.csv`,
      ['Hora', 'Latitude', 'Longitude', 'Velocidade (km/h)', 'Curso', 'Endereço'],
      rows.map((p) => [
        formatDateTime(p.fixTime),
        p.latitude?.toFixed(6) ?? '',
        p.longitude?.toFixed(6) ?? '',
        ((p.speed ?? 0) * 1.852).toFixed(0),
        p.course != null ? Math.round(p.course) : '',
        formatAddress(p.address),
      ]),
    );
  };

  return (
    <ReportShell
      title="Rota da embarcação"
      description="Histórico de posições no período (uma embarcação por vez)."
    >
      <ReportFilters
        deviceMode="single"
        deviceId={deviceId}
        onDeviceChange={setDeviceId}
        preset={preset}
        onPresetChange={setPreset}
        onGenerate={generate}
        generating={query.isFetching}
      />
      {executed && (
        <ResultBar
          loading={query.isLoading}
          count={rows.length}
          unit="posições"
          onExport={handleExport}
        />
      )}
      {rows.length > 0 && <RouteReplayMap positions={rows} />}
      <ReportTable
        notRun={executed === null}
        loading={query.isLoading}
        error={query.error}
        empty={rows.length === 0}
      >
        {/* rolagem horizontal: tabela larga nao pode empurrar a pagina inteira no celular */}
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-semibold">Hora</th>
              <th className="px-4 py-2 font-semibold">Coordenadas</th>
              <th className="px-4 py-2 font-semibold">Velocidade</th>
              <th className="px-4 py-2 font-semibold">Endereço</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 1000).map((p, i) => (
              <tr key={`${p.id ?? i}-${i}`} className="border-b border-border last:border-0 hover:bg-accent/40">
                <td className="whitespace-nowrap px-4 py-2 text-xs">{formatDateTime(p.fixTime)}</td>
                <td className="px-4 py-2 font-mono text-[11px]">
                  {formatCoords(p.latitude, p.longitude)}
                </td>
                <td className="px-4 py-2">{knotsToKmh(p.speed)}</td>
                <td className="px-4 py-2 text-xs">{formatAddress(p.address) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {rows.length > 1000 && (
          <p className="border-t border-border p-3 text-center text-xs text-muted-foreground">
            Exibindo primeiras 1000 posições. Use o CSV pra ver tudo.
          </p>
        )}
      </ReportTable>
    </ReportShell>
  );
}
